import { Controller } from "@hotwired/stimulus"

/**
 * Detail Modal Controller
 * 
 * n8n-style fullscreen modal for viewing/editing node details.
 * Two-pane layout:
 * - Left: INPUT tree showing template variables ({{$.field}})
 * - Right: Parameters tab (editable fields) + JSON tab (syntax highlighted)
 */
export default class extends Controller {
  static targets = [
    "container", "icon", "title", "subtitle",
    "treeView", "parametersTab", "jsonTab",
    "parametersTabBtn", "jsonTabBtn",
    "editLink", "statusText"
  ]
  
  static values = {
    editUrl: String
  }

  connect() {
    console.log('[DetailModal] Connected')
    this.currentTab = 'parameters'
    this.nodeData = null
  }

  // Called from action_preview_controller when a node is clicked
  openWithData(nodeData) {
    console.log('[DetailModal] Opening with data:', nodeData)
    this.nodeData = nodeData
    
    // Show modal
    this.element.classList.remove('hidden')
    document.body.style.overflow = 'hidden'
    
    // Render based on node type
    switch (nodeData.type) {
      case 'action':
        this.renderActionModal(nodeData.action)
        break
      case 'trigger':
        this.renderTriggerModal(nodeData.trigger)
        break
      case 'outlet':
        this.renderOutletModal(nodeData.outlet)
        break
      case 'block':
        this.renderBlockModal(nodeData.block, nodeData.index)
        break
      default:
        console.warn('[DetailModal] Unknown node type:', nodeData.type)
    }
    
    // Reset to parameters tab
    this.currentTab = 'parameters'
    this.updateTabUI()
  }

  close() {
    console.log('[DetailModal] Closing')
    this.element.classList.add('hidden')
    document.body.style.overflow = ''
    this.nodeData = null
  }

  closeOnBackdrop(event) {
    if (event.target === this.element) {
      this.close()
    }
  }

  stopPropagation(event) {
    event.stopPropagation()
  }

  handleKeydown(event) {
    if (event.key === 'Escape' && !this.element.classList.contains('hidden')) {
      this.close()
    }
  }

  switchTab(event) {
    const tab = event.currentTarget.dataset.tab
    this.currentTab = tab
    this.updateTabUI()
  }

  updateTabUI() {
    const isParameters = this.currentTab === 'parameters'
    
    // Show/hide tab content
    if (this.hasParametersTabTarget) {
      this.parametersTabTarget.classList.toggle('hidden', !isParameters)
    }
    if (this.hasJsonTabTarget) {
      this.jsonTabTarget.classList.toggle('hidden', isParameters)
    }
    
    // Update tab button styles
    if (this.hasParametersTabBtnTarget) {
      this.parametersTabBtnTarget.classList.toggle('bg-gray-700', isParameters)
      this.parametersTabBtnTarget.classList.toggle('text-white', isParameters)
      this.parametersTabBtnTarget.classList.toggle('text-gray-400', !isParameters)
    }
    if (this.hasJsonTabBtnTarget) {
      this.jsonTabBtnTarget.classList.toggle('bg-gray-700', !isParameters)
      this.jsonTabBtnTarget.classList.toggle('text-white', !isParameters)
      this.jsonTabBtnTarget.classList.toggle('text-gray-400', isParameters)
    }
  }

  // ==================== Action Modal ====================
  renderActionModal(action) {
    const colors = this.getActionTypeColors(action.action_type)
    const icon = this.getActionTypeIcon(action.action_type)
    
    // Header
    this.iconTarget.style.backgroundColor = colors.icon
    this.iconTarget.innerHTML = `<span class="text-lg">${icon}</span>`
    this.titleTarget.textContent = action.name
    this.subtitleTarget.textContent = action.type_label || action.action_type
    
    // Edit link
    this.editLinkTarget.href = this.editUrlValue
    
    // Status text
    this.statusTextTarget.innerHTML = `
      <span class="${action.active ? 'text-emerald-400' : 'text-gray-500'}">
        ${action.active ? '● Active' : '○ Inactive'}
      </span>
      <span class="mx-2 text-gray-600">|</span>
      <span>${action.execution_count || 0} executions</span>
    `
    
    // Input tree (template variables)
    this.renderInputTree(action)
    
    // Parameters tab (type-specific)
    this.renderActionParameters(action)
    
    // JSON tab
    this.renderJsonTab(action)
  }

  renderActionParameters(action) {
    let html = ''
    
    switch (action.action_type) {
      case 'slack_message':
        html = this.renderSlackMessageParams(action)
        break
      case 'http_forward':
        html = this.renderHttpForwardParams(action)
        break
      case 'email':
        html = this.renderEmailParams(action)
        break
      case 'agent':
        html = this.renderAgentParams(action)
        break
      default:
        html = this.renderGenericParams(action)
    }
    
    this.parametersTabTarget.innerHTML = html
  }

  renderSlackMessageParams(action) {
    const blocks = action.blocks || []
    return `
      <div class="space-y-6">
        <div class="grid grid-cols-2 gap-4">
          ${this.renderField('Channel', action.channel || 'Not set', 'channel')}
          ${this.renderField('Template', action.template_name || 'Custom', 'template')}
        </div>
        
        <div>
          <label class="block text-sm font-medium text-gray-400 mb-2">Blocks (${blocks.length})</label>
          <div class="space-y-2">
            ${blocks.map((block, i) => `
              <div class="flex items-center gap-3 p-3 bg-gray-800 rounded-lg border border-gray-700">
                <div class="w-8 h-8 rounded flex items-center justify-center ${this.getBlockBgClass(block.type)}">
                  <span class="text-sm">${this.getBlockIcon(block.type)}</span>
                </div>
                <div class="flex-1">
                  <div class="text-sm text-white">${block.type}</div>
                  <div class="text-xs text-gray-500">${this.getBlockPreview(block)}</div>
                </div>
                <span class="text-xs text-gray-600">#${i + 1}</span>
              </div>
            `).join('')}
            ${blocks.length === 0 ? '<p class="text-sm text-gray-500 italic">No blocks defined</p>' : ''}
          </div>
        </div>
      </div>
    `
  }

  renderHttpForwardParams(action) {
    const config = action.config || {}
    return `
      <div class="space-y-6">
        <div class="grid grid-cols-2 gap-4">
          ${this.renderField('Method', config.method || 'POST', 'method')}
          ${this.renderField('Content-Type', config.content_type || 'application/json', 'content_type')}
        </div>
        
        ${this.renderField('URL', config.url || 'Not configured', 'url', true)}
        
        ${config.headers ? `
          <div>
            <label class="block text-sm font-medium text-gray-400 mb-2">Headers</label>
            <pre class="p-3 bg-gray-800 rounded-lg text-sm text-gray-300 overflow-x-auto">${JSON.stringify(config.headers, null, 2)}</pre>
          </div>
        ` : ''}
        
        ${config.body_template ? `
          <div>
            <label class="block text-sm font-medium text-gray-400 mb-2">Body Template</label>
            <pre class="p-3 bg-gray-800 rounded-lg text-sm text-gray-300 overflow-x-auto">${this.escapeHtml(config.body_template)}</pre>
          </div>
        ` : ''}
      </div>
    `
  }

  renderEmailParams(action) {
    const config = action.config || {}
    return `
      <div class="space-y-6">
        ${this.renderField('To', config.to || 'Not set', 'to', true)}
        ${this.renderField('Subject', config.subject || 'Not set', 'subject', true)}
        
        ${config.body_template ? `
          <div>
            <label class="block text-sm font-medium text-gray-400 mb-2">Body Template</label>
            <pre class="p-3 bg-gray-800 rounded-lg text-sm text-gray-300 overflow-x-auto whitespace-pre-wrap">${this.escapeHtml(config.body_template)}</pre>
          </div>
        ` : ''}
      </div>
    `
  }

  renderAgentParams(action) {
    const config = action.config || {}
    return `
      <div class="space-y-6">
        ${this.renderField('Agent ID', config.agent_id || 'Not set', 'agent_id')}
        ${this.renderField('Provider', config.provider || 'Claude', 'provider')}
        
        ${config.prompt_template ? `
          <div>
            <label class="block text-sm font-medium text-gray-400 mb-2">Prompt Template</label>
            <pre class="p-3 bg-gray-800 rounded-lg text-sm text-gray-300 overflow-x-auto whitespace-pre-wrap">${this.escapeHtml(config.prompt_template)}</pre>
          </div>
        ` : ''}
      </div>
    `
  }

  renderGenericParams(action) {
    const config = action.config || {}
    return `
      <div class="space-y-6">
        <div class="grid grid-cols-2 gap-4">
          ${this.renderField('Type', action.action_type, 'type')}
          ${this.renderField('Status', action.active ? 'Active' : 'Inactive', 'status')}
        </div>
        
        ${Object.keys(config).length > 0 ? `
          <div>
            <label class="block text-sm font-medium text-gray-400 mb-2">Configuration</label>
            <pre class="p-3 bg-gray-800 rounded-lg text-sm text-gray-300 overflow-x-auto">${JSON.stringify(config, null, 2)}</pre>
          </div>
        ` : '<p class="text-gray-500 italic">No additional configuration</p>'}
      </div>
    `
  }

  // ==================== Trigger Modal ====================
  renderTriggerModal(trigger) {
    // Header
    this.iconTarget.style.backgroundColor = '#10b981' // emerald-500
    this.iconTarget.innerHTML = `<span class="text-lg">${this.getTriggerIcon(trigger.source_type)}</span>`
    this.titleTarget.textContent = trigger.name
    this.subtitleTarget.textContent = trigger.parent_name || trigger.source_type
    
    // Status text
    this.statusTextTarget.innerHTML = `
      <span class="${trigger.active ? 'text-emerald-400' : 'text-gray-500'}">
        ${trigger.active ? '● Active' : '○ Inactive'}
      </span>
      <span class="mx-2 text-gray-600">|</span>
      <span>${trigger.execution_count || 0} executions</span>
    `
    
    // Input tree
    this.treeViewTarget.innerHTML = `
      <div class="text-gray-400 text-sm">
        <p class="mb-4">Triggers don't have input fields - they are the source of data.</p>
        <p class="text-xs text-gray-500">The data from this trigger flows into the connected action.</p>
      </div>
    `
    
    // Parameters tab
    this.parametersTabTarget.innerHTML = `
      <div class="space-y-6">
        <div class="grid grid-cols-2 gap-4">
          ${this.renderField('Source Type', trigger.source_type, 'source_type')}
          ${this.renderField('Status', trigger.active ? 'Active' : 'Inactive', 'status')}
        </div>
        ${this.renderField('Parent', trigger.parent_name || 'None', 'parent', true)}
      </div>
    `
    
    // JSON tab
    this.renderJsonTab(trigger)
  }

  // ==================== Outlet Modal ====================
  renderOutletModal(outlet) {
    const colors = this.getOutletStyleColors(outlet.style)
    
    // Header
    this.iconTarget.style.backgroundColor = colors.border
    this.iconTarget.innerHTML = `<span class="text-lg">${outlet.outlet_type === 'button' ? '🔘' : '📋'}</span>`
    this.titleTarget.textContent = outlet.label
    this.subtitleTarget.textContent = `${outlet.outlet_type} outlet`
    
    // Status text
    const chainText = outlet.has_chain ? `→ ${outlet.chain_target}` : 'No chain'
    this.statusTextTarget.innerHTML = `
      <span class="text-gray-400">${chainText}</span>
    `
    
    // Input tree
    this.treeViewTarget.innerHTML = `
      <div class="text-gray-400 text-sm">
        <p class="mb-4">Outlets capture user interactions and can trigger action chains.</p>
        ${outlet.has_chain ? `
          <div class="p-3 bg-purple-900/30 border border-purple-700 rounded-lg">
            <p class="text-purple-300 text-xs font-medium">CHAINS TO</p>
            <p class="text-white mt-1">${outlet.chain_target}</p>
          </div>
        ` : `
          <p class="text-xs text-gray-500">No action chain configured for this outlet.</p>
        `}
      </div>
    `
    
    // Parameters tab
    this.parametersTabTarget.innerHTML = `
      <div class="space-y-6">
        <div class="grid grid-cols-2 gap-4">
          ${this.renderField('Type', outlet.outlet_type, 'outlet_type')}
          ${this.renderField('Style', outlet.style || 'default', 'style')}
        </div>
        ${this.renderField('Label', outlet.label, 'label', true)}
        ${this.renderField('Value', outlet.value || outlet.label, 'value', true)}
        ${outlet.has_chain ? 
          this.renderField('Chain Target', outlet.chain_target, 'chain_target', true) : 
          '<p class="text-gray-500 italic">No action chain configured</p>'
        }
      </div>
    `
    
    // JSON tab
    this.renderJsonTab(outlet)
  }

  // ==================== Block Modal ====================
  renderBlockModal(block, index) {
    const style = this.getBlockStyle(block.type)
    
    // Header
    this.iconTarget.style.backgroundColor = style.color
    this.iconTarget.innerHTML = `<span class="text-lg">${style.icon}</span>`
    this.titleTarget.textContent = `Block #${index + 1}`
    this.subtitleTarget.textContent = block.type
    
    // Status text
    this.statusTextTarget.innerHTML = `<span class="text-gray-400">${block.type} block</span>`
    
    // Input tree (show template variables in block)
    const variables = this.extractTemplateVariables(JSON.stringify(block))
    this.treeViewTarget.innerHTML = variables.length > 0 ? `
      <div class="space-y-2">
        <p class="text-xs text-gray-500 mb-3">Template variables in this block:</p>
        ${variables.map(v => `
          <div class="flex items-center gap-2 p-2 bg-gray-800 rounded text-xs">
            <span class="text-purple-400 font-mono">${this.escapeHtml(v)}</span>
          </div>
        `).join('')}
      </div>
    ` : `
      <p class="text-gray-500 text-sm italic">No template variables in this block</p>
    `
    
    // Parameters tab
    this.parametersTabTarget.innerHTML = `
      <div class="space-y-6">
        ${this.renderField('Block Type', block.type, 'type')}
        ${this.renderBlockContent(block)}
      </div>
    `
    
    // JSON tab
    this.renderJsonTab(block)
  }

  renderBlockContent(block) {
    switch (block.type) {
      case 'header':
        return this.renderField('Text', block.text?.text || '', 'header_text', true)
      case 'section':
        return `
          ${block.text ? this.renderField('Text', block.text.text || '', 'section_text', true) : ''}
          ${block.fields ? `
            <div>
              <label class="block text-sm font-medium text-gray-400 mb-2">Fields (${block.fields.length})</label>
              <div class="space-y-2">
                ${block.fields.map((f, i) => `
                  <div class="p-2 bg-gray-800 rounded text-sm text-gray-300">${this.escapeHtml(f.text || '')}</div>
                `).join('')}
              </div>
            </div>
          ` : ''}
        `
      case 'context':
        return block.elements ? `
          <div>
            <label class="block text-sm font-medium text-gray-400 mb-2">Elements (${block.elements.length})</label>
            <div class="space-y-2">
              ${block.elements.map((e, i) => `
                <div class="p-2 bg-gray-800 rounded text-sm text-gray-300">
                  ${e.type === 'image' ? `<img src="${e.image_url}" class="h-6 inline mr-2"/>` : ''}
                  ${e.text || e.alt_text || e.type}
                </div>
              `).join('')}
            </div>
          </div>
        ` : ''
      case 'divider':
        return '<p class="text-gray-500 italic">Divider blocks have no additional parameters</p>'
      case 'image':
        return `
          ${this.renderField('Alt Text', block.alt_text || '', 'alt_text', true)}
          ${this.renderField('Image URL', block.image_url || '', 'image_url', true)}
        `
      case 'actions':
        return block.elements ? `
          <div>
            <label class="block text-sm font-medium text-gray-400 mb-2">Action Elements (${block.elements.length})</label>
            <div class="space-y-2">
              ${block.elements.map((e, i) => `
                <div class="flex items-center gap-2 p-2 bg-gray-800 rounded">
                  <span class="text-xs px-2 py-0.5 bg-gray-700 rounded">${e.type}</span>
                  <span class="text-sm text-gray-300">${e.text?.text || e.placeholder?.text || 'Unnamed'}</span>
                </div>
              `).join('')}
            </div>
          </div>
        ` : ''
      default:
        return `<pre class="p-3 bg-gray-800 rounded text-sm text-gray-300 overflow-x-auto">${JSON.stringify(block, null, 2)}</pre>`
    }
  }

  // ==================== Shared Renderers ====================
  renderInputTree(action) {
    // Extract template variables from the action config
    const configStr = JSON.stringify(action.config || {})
    const blocksStr = JSON.stringify(action.blocks || [])
    const allContent = configStr + blocksStr
    
    const variables = this.extractTemplateVariables(allContent)
    
    if (variables.length === 0) {
      this.treeViewTarget.innerHTML = `
        <p class="text-gray-500 text-sm italic">No template variables found</p>
        <p class="text-gray-600 text-xs mt-2">Use {{$.field}} syntax to reference incoming data</p>
      `
      return
    }
    
    // Group variables by path
    const grouped = this.groupVariablesByPath(variables)
    
    this.treeViewTarget.innerHTML = `
      <div class="space-y-1">
        <div class="flex items-center gap-2 text-gray-400 mb-3">
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"/>
          </svg>
          <span class="text-xs font-medium">INPUT</span>
        </div>
        ${this.renderVariableTree(grouped)}
      </div>
    `
  }

  extractTemplateVariables(text) {
    const regex = /\{\{\s*\$\.([^}]+)\s*\}\}/g
    const variables = new Set()
    let match
    while ((match = regex.exec(text)) !== null) {
      variables.add(`{{$.${match[1]}}}`)
    }
    return Array.from(variables)
  }

  groupVariablesByPath(variables) {
    const root = { children: {}, variables: [] }
    
    for (const v of variables) {
      // Extract path from {{$.path.to.field}}
      const match = v.match(/\{\{\s*\$\.(.+?)\s*\}\}/)
      if (!match) continue
      
      const path = match[1].split('.')
      let current = root
      
      for (let i = 0; i < path.length; i++) {
        const part = path[i]
        if (i === path.length - 1) {
          // Leaf node
          current.variables.push({ name: part, full: v })
        } else {
          // Branch node
          if (!current.children[part]) {
            current.children[part] = { children: {}, variables: [] }
          }
          current = current.children[part]
        }
      }
    }
    
    return root
  }

  renderVariableTree(node, depth = 0) {
    let html = ''
    const indent = depth * 16
    
    // Render child branches
    for (const [name, child] of Object.entries(node.children)) {
      html += `
        <div style="margin-left: ${indent}px">
          <div class="flex items-center gap-2 py-1 text-gray-300 hover:bg-gray-800 rounded px-2 cursor-pointer">
            <svg class="w-3 h-3 text-gray-500" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"/>
            </svg>
            <span class="text-sm">${name}</span>
          </div>
          ${this.renderVariableTree(child, depth + 1)}
        </div>
      `
    }
    
    // Render leaf variables
    for (const v of node.variables) {
      html += `
        <div style="margin-left: ${indent}px" 
             class="flex items-center gap-2 py-1 px-2 hover:bg-gray-800 rounded cursor-pointer group"
             title="Click to copy: ${v.full}">
          <span class="w-3 h-3 rounded bg-purple-600"></span>
          <span class="text-sm text-purple-300 font-mono">${v.name}</span>
          <span class="text-xs text-gray-600 opacity-0 group-hover:opacity-100">click to copy</span>
        </div>
      `
    }
    
    return html
  }

  renderJsonTab(data) {
    const json = JSON.stringify(data, null, 2)
    const lines = json.split('\n')
    
    this.jsonTabTarget.innerHTML = `
      <div class="flex text-sm font-mono">
        <div class="py-4 px-3 text-right text-gray-600 select-none bg-gray-900 border-r border-gray-800">
          ${lines.map((_, i) => `<div>${i + 1}</div>`).join('')}
        </div>
        <pre class="flex-1 p-4 overflow-x-auto text-gray-300">${this.syntaxHighlight(json)}</pre>
      </div>
    `
  }

  syntaxHighlight(json) {
    return json
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/("(\\u[a-zA-Z0-9]{4}|\\[^u]|[^\\"])*"(\s*:)?|\b(true|false|null)\b|-?\d+(?:\.\d*)?(?:[eE][+\-]?\d+)?)/g, (match) => {
        let cls = 'text-amber-400' // number
        if (/^"/.test(match)) {
          if (/:$/.test(match)) {
            cls = 'text-blue-400' // key
          } else {
            cls = 'text-emerald-400' // string
          }
        } else if (/true|false/.test(match)) {
          cls = 'text-purple-400' // boolean
        } else if (/null/.test(match)) {
          cls = 'text-gray-500' // null
        }
        return `<span class="${cls}">${match}</span>`
      })
  }

  renderField(label, value, fieldName, fullWidth = false) {
    return `
      <div class="${fullWidth ? '' : ''}">
        <label class="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">${label}</label>
        <div class="p-3 bg-gray-800 rounded-lg border border-gray-700 text-sm text-white">
          ${this.escapeHtml(value)}
        </div>
      </div>
    `
  }

  // ==================== Helpers ====================
  getActionTypeColors(type) {
    const colors = {
      slack_message: { bg: '#1e1b4b', border: '#7c3aed', icon: '#7c3aed' },
      http_forward: { bg: '#172554', border: '#3b82f6', icon: '#3b82f6' },
      log: { bg: '#422006', border: '#f59e0b', icon: '#f59e0b' },
      email: { bg: '#052e16', border: '#22c55e', icon: '#22c55e' },
      agent: { bg: '#2e1065', border: '#a855f7', icon: '#a855f7' }
    }
    return colors[type] || colors.log
  }

  getActionTypeIcon(type) {
    const icons = {
      slack_message: '⚡',
      http_forward: '🔗',
      log: '📝',
      email: '✉️',
      agent: '🤖'
    }
    return icons[type] || '⚡'
  }

  getTriggerIcon(sourceType) {
    const icons = {
      webhook: '🔗',
      schedule: '⏰',
      slack: '💬',
      email: '✉️'
    }
    return icons[sourceType] || '⚡'
  }

  getOutletStyleColors(style) {
    const colors = {
      primary: { bg: '#1e3a8a', border: '#3b82f6' },
      danger: { bg: '#7f1d1d', border: '#ef4444' },
      default: { bg: '#1f2937', border: '#4b5563' }
    }
    return colors[style] || colors.default
  }

  getBlockStyle(type) {
    const styles = {
      header: { color: '#a78bfa', bgColor: '#2e1065', icon: 'H' },
      section: { color: '#60a5fa', bgColor: '#172554', icon: '§' },
      divider: { color: '#9ca3af', bgColor: '#1f2937', icon: '—' },
      context: { color: '#c084fc', bgColor: '#2e1065', icon: 'i' },
      image: { color: '#4ade80', bgColor: '#052e16', icon: '🖼' },
      actions: { color: '#fbbf24', bgColor: '#422006', icon: '⚡' }
    }
    return styles[type] || { color: '#9ca3af', bgColor: '#1f2937', icon: '?' }
  }

  getBlockBgClass(type) {
    const classes = {
      header: 'bg-purple-900',
      section: 'bg-blue-900',
      divider: 'bg-gray-700',
      context: 'bg-purple-900',
      image: 'bg-emerald-900',
      actions: 'bg-amber-900'
    }
    return classes[type] || 'bg-gray-700'
  }

  getBlockIcon(type) {
    const icons = {
      header: 'H',
      section: '§',
      divider: '—',
      context: 'i',
      image: '🖼',
      actions: '⚡'
    }
    return icons[type] || '?'
  }

  getBlockPreview(block) {
    switch (block.type) {
      case 'header':
        return this.truncate(block.text?.text || '', 30)
      case 'section':
        return this.truncate(block.text?.text || `${block.fields?.length || 0} fields`, 30)
      case 'context':
        return `${block.elements?.length || 0} elements`
      case 'divider':
        return 'Visual separator'
      case 'image':
        return this.truncate(block.alt_text || 'Image', 30)
      case 'actions':
        return `${block.elements?.length || 0} buttons`
      default:
        return block.type
    }
  }

  truncate(text, maxLength) {
    if (!text) return ''
    return text.length > maxLength ? text.slice(0, maxLength) + '...' : text
  }

  escapeHtml(text) {
    if (!text) return ''
    const div = document.createElement('div')
    div.textContent = text
    return div.innerHTML
  }
}
